import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useCrmFilterValues } from '../../../../features/crm-integration/hooks/use-crm-filter-values';
import { useCrmIntegration } from '../../../../features/crm-integration/hooks/use-crm-integration';
import { useCustomerIntelligenceFilters } from '../../../../features/insights/hooks/useCustomerIntelligenceFilters';
import { SelectAll } from '../SelectAll';
import { CurrencyFilter } from '../CurrencyFilter';

export const CRMFilters: React.FC = () => {
  const { filters: crmFilters } = useCrmFilterValues();
  const { workspaceCrmIntegration } = useCrmIntegration();
  const { setCrmMap, crmFilters: crmFiltersValues } = useCustomerIntelligenceFilters();

  const [values, setValues] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    const areStoredDataEmpty = Array.from(crmFiltersValues.values()).every(
      value => value.length === 0,
    );
    const areLocalValuesFilled = Array.from(values.values()).some(value => value.length > 0);
    if (areStoredDataEmpty && areLocalValuesFilled) {
      const newValues = new Map<string, string[]>();
      for (const key of Array.from(values.keys())) {
        newValues.set(key, []);
      }
      setValues(newValues);
    }
  }, [crmFiltersValues]);

  const components = useMemo(() => {
    const newComps: ReactElement[] = [];

    for (const filt in crmFilters) {
      const label = workspaceCrmIntegration
        ? workspaceCrmIntegration.workspace.properties.find(p => p.id === filt)?.label || filt
        : filt;

      const options = crmFilters[filt].map(opt => ({
        id: opt,
        label: opt,
      }));

      if (filt.toLowerCase().includes('revenue')) {
        const optionsValues = options
          .map(option => Number(option.id))
          .filter(option => typeof option === 'number' && !isNaN(option));

        if (optionsValues.length > 0) {
          const min = Math.min(...optionsValues);
          const max = Math.max(...optionsValues);

          newComps.push(
            <CurrencyFilter
              label={label}
              min={min}
              max={max}
              onSelectionChanged={(min: number, max: number) => {
                const optionsToFilter = optionsValues
                  .filter(option => option >= min && option <= max)
                  .map(option => option.toString());
                setValues(prevValue => new Map(prevValue).set(filt, optionsToFilter));
              }}
            />,
          );
        }
      } else {
        newComps.push(
          <SelectAll
            key={filt}
            label={label}
            options={options}
            value={values.get(filt) || []}
            showAll={true}
            style={{ width: '220px', maxHeight: '32px' }}
            onSelectionChanged={selectedValues => {
              setValues(prevValue => new Map(prevValue).set(filt, selectedValues));
            }}
          />,
        );
      }
    }

    return newComps;
  }, [workspaceCrmIntegration, crmFilters, values, crmFiltersValues]);

  useEffect(() => setCrmMap(values), [values]);

  return <>{components}</>;
};
